import { Injectable } from '@angular/core';
import { Estimation } from '../../models/estimacion.model';
import { User } from '../../models/user.model';
import {
  ExportPayload,
  ExportSummary,
  ExportTaskRecord
} from '../models/export-payload';
import {
  ExportProvenance,
  normalizeProvenance,
  provenanceLabel
} from '../models/provenance';

/**
 * Construye un ExportPayload a partir del estado en memoria del componente
 * (siempre el Borrador actual — no hace fetch al backend).
 *
 * Toda la matemática de agregados (totales por perfil, por sistema, conteo
 * por origen) se computa aquí UNA sola vez; los exporters la consumen del
 * `summary` sin recalcular.
 */
@Injectable({ providedIn: 'root' })
export class ExportPayloadBuilder {

  build(estimation: Estimation, user: User | null | undefined): ExportPayload {
    const exportedAt = new Date().toISOString();
    const exportedBy = {
      id: (user?.id ?? 'unknown').toString(),
      name: user?.username ?? 'desconocido'
    };

    const tasks = this.flattenTasks(estimation);

    return {
      estimation: {
        id: ((estimation?.id as unknown) ?? 'unsaved').toString(),
        name: (estimation as any)?.name,
        promptExcerpt: this.excerpt((estimation as any)?.prompt, 280),
        generatedAt: this.tryIso((estimation as any)?.createdat),
        exportedAt,
        exportedBy,
        schemaVersion: '1.0'
      },
      tasks,
      summary: this.computeSummary(tasks)
    };
  }

  private flattenTasks(estimation: Estimation): ExportTaskRecord[] {
    const out: ExportTaskRecord[] = [];
    const requirements: any[] = (estimation as any)?.requirements ?? [];

    requirements.forEach((req: any, ri: number) => {
      const reqId = (req?.id ?? `req-${ri}`).toString();
      const reqDesc: string = req?.description ?? '';
      const tasks: any[] = req?.tasks ?? [];

      tasks.forEach((t: any, ti: number) => {
        const provenance = normalizeProvenance(t?.source);
        out.push({
          id: (t?.id ?? `${reqId}-task-${ti}`).toString(),
          requirementId: reqId,
          requirementDescription: reqDesc,
          description: t?.description ?? '',
          type: t?.type ?? '',
          subsystem: t?.subsystem ?? '',
          technology: t?.technology ?? '',
          profile: t?.profile ?? '',
          hours: Number(t?.estimatetime ?? 0),
          p10Hours: typeof t?.p10_hours === 'number' ? t.p10_hours : undefined,
          p90Hours: typeof t?.p90_hours === 'number' ? t.p90_hours : undefined,
          confidence: t?.confidence,
          provenance,
          provenanceLabel: provenanceLabel(provenance),
          sourceRaw: t?.source
        });
      });
    });

    return out;
  }

  private excerpt(text: string | undefined, maxChars: number): string | undefined {
    if (!text) return undefined;
    const t = text.trim();
    return t.length <= maxChars ? t : t.slice(0, maxChars - 1) + '…';
  }

  private tryIso(value: unknown): string | undefined {
    if (!value) return undefined;
    try {
      const d = new Date(value as any);
      if (isNaN(d.getTime())) return undefined;
      return d.toISOString();
    } catch {
      return undefined;
    }
  }

  private computeSummary(tasks: ExportTaskRecord[]): ExportSummary {
    const summary: ExportSummary = {
      taskCount: tasks.length,
      totalHours: 0,
      hoursByProfile: {},
      hoursBySystem: {},
      provenanceCounts: {
        [ExportProvenance.AI]: 0,
        [ExportProvenance.AI_MODIFIED]: 0,
        [ExportProvenance.HISTORIC]: 0,
        [ExportProvenance.HISTORIC_MODIFIED]: 0,
        [ExportProvenance.MANUAL]: 0,
        [ExportProvenance.MANUAL_MODIFIED]: 0,
        [ExportProvenance.UNKNOWN]: 0
      }
    };

    for (const t of tasks) {
      summary.totalHours += t.hours;
      const profKey = t.profile || '(sin perfil)';
      const sysKey = t.subsystem || '(sin subsistema)';
      summary.hoursByProfile[profKey] = (summary.hoursByProfile[profKey] ?? 0) + t.hours;
      summary.hoursBySystem[sysKey] = (summary.hoursBySystem[sysKey] ?? 0) + t.hours;
      summary.provenanceCounts[t.provenance] += 1;
    }

    return summary;
  }
}
